import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { fullDate } from '../net/apiKeys';
import {
  changedFields,
  checklist,
  getOrganization,
  getReadiness,
  plain,
  profileFields,
  requestVerification,
  toGo,
  updateProfile,
  verificationOf,
  type Check,
  type Organization,
  type ProfileField,
  type Readiness,
} from '../net/brandProfile';
import { ApiError } from '../net/client';
import { useProfileGaps } from '../session/profileGaps';
import { useSession, useSessionState } from '../session/useSession';
import { colors, radius, shell, type, verdict } from '../theme';

/**
 * G11, `design/PortalSettings.dc.html`.
 *
 * The organization and its readiness, loaded together. The left card is the
 * manufacturer profile; Save changes `PATCH`es the fields that changed, then
 * reads readiness again so the checklist and the sidebar badge follow. The
 * navy panel draws the one verification state the org is in, and Request
 * verification `POST`s the optional note. Reading is open to any org user,
 * the writes are admin only, so a member sees the same page read only, by
 * the user's choice. D17.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; organization: Organization; readiness: Readiness };

type Draft = Record<ProfileField, string>;

const draftOf = (organization: Organization): Draft =>
  Object.fromEntries(
    profileFields.map((f) => [f, organization.manufacturerProfile?.[f] ?? '']),
  ) as Draft;

/** The board's labels, verbatim. */
const labels: Record<ProfileField, string> = {
  legalName: 'Registered legal name',
  registrationNumber: 'Registration / licence number',
  country: 'Country of manufacture',
  supportEmail: 'Support email',
  supportPhone: 'Support phone',
  website: 'Website',
};

/** Literal in the board's verification panel and in no token. */
const onNavy = {
  chip: 'rgba(255,255,255,0.12)',
  field: 'rgba(255,255,255,0.08)',
  fieldBorder: 'rgba(255,255,255,0.18)',
  rule: 'rgba(255,255,255,0.14)',
  dimmed: 'rgba(255,255,255,0.35)',
  awaiting: 'rgba(252,243,217,0.92)',
};

export function BrandProfile() {
  const state = useSessionState();
  const isAdmin = state.status === 'signed_in' && state.user.role === 'org_admin';
  const { api } = useSession();
  const gaps = useProfileGaps();
  const setGaps = gaps.set;
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ProfileField, string>>>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [askRefusal, setAskRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([getOrganization(api, signal), getReadiness(api, signal)]).then(
      ([organization, readiness]) => {
        setLoad({ status: 'loaded', organization, readiness });
        setDraft(draftOf(organization));
        setGaps(toGo(readiness));
      },
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api, setGaps]);

  if (load.status === 'failed') {
    return (
      <>
        <PageHeader title="Brand profile" subtitle={subtitle} />
        <Card title="The brand profile could not be loaded" body={failureText(load.error)} />
      </>
    );
  }

  const organization = load.status === 'loaded' ? load.organization : null;
  const changes =
    organization && draft ? changedFields(organization.manufacturerProfile, draft) : {};
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!organization || !dirty) return;
    setBusy(true);
    setRefusal(null);
    setFieldErrors({});
    try {
      const saved = await updateProfile(api, changes);
      setDraft(draftOf(saved));
      setLoad((current) =>
        current.status === 'loaded' ? { ...current, organization: saved } : current,
      );
      const readiness = await getReadiness(api).catch(() => null);
      if (readiness) {
        setLoad((current) => (current.status === 'loaded' ? { ...current, readiness } : current));
        setGaps(toGo(readiness));
      }
    } catch (error) {
      const landed = fromFields(error);
      setFieldErrors(landed.fields);
      setRefusal(landed.general ?? null);
    }
    setBusy(false);
  };

  const ask = async () => {
    setBusy(true);
    setAskRefusal(null);
    try {
      const updated = await requestVerification(api, note.trim());
      setLoad((current) =>
        current.status === 'loaded' ? { ...current, organization: updated } : current,
      );
      setNote('');
    } catch (error) {
      setAskRefusal(askFailureText(error));
    }
    setBusy(false);
  };

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading your brand profile</Waiting> : null}
      <PageHeader
        title="Brand profile"
        subtitle={subtitle}
        action={
          isAdmin ? (
            <button
              className="vp-fill"
              type="button"
              disabled={busy || !dirty}
              onClick={() => void save()}
              style={actionButton}
            >
              Save changes
            </button>
          ) : undefined
        }
      />
      <div aria-busy={load.status === 'loading'} style={columns}>
        <section
          aria-labelledby="profile-title"
          style={{ ...cardBox, padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <h2 id="profile-title" style={{ ...type.panelTitle, color: colors.navy, margin: 0 }}>
            Who stands behind your products
          </h2>
          {isAdmin ? null : (
            <p style={{ ...type.caption, color: colors.mutedInk, margin: 0 }}>
              Only organization admins can change the brand profile.
            </p>
          )}
          {refusal !== null ? <Rejection>{refusal}</Rejection> : null}
          {draft ? (
            <>
              {field(
                'legalName',
                'Shown on every genuine scan: this is how a consumer tells you from an imitator.',
              )}
              <div style={pair}>
                {field('registrationNumber')}
                {field('country')}
              </div>
              <div style={pair}>
                {field('supportEmail', undefined, 'email')}
                {field('supportPhone', undefined, 'tel')}
              </div>
              {field('website', undefined, 'url')}
            </>
          ) : null}
          <p style={{ ...type.caption, color: colors.mutedInk, lineHeight: 1.5, margin: 0 }}>
            A consumer who finds a fake needs a way to reach you. Email or phone: at least one is
            required.
          </p>
        </section>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {load.status === 'loaded' ? (
            <>
              <Checklist
                rows={checklist(load.readiness, load.organization.manufacturerProfile)}
                onFix={(to) => navigate(`/${to}`)}
              />
              <VerificationPanel
                organization={load.organization}
                readiness={load.readiness}
                isAdmin={isAdmin}
                busy={busy}
                note={note}
                onNote={setNote}
                onAsk={() => void ask()}
                refusal={askRefusal}
              />
            </>
          ) : null}
        </div>
      </div>
    </>
  );

  function field(name: ProfileField, help?: string, inputType = 'text'): ReactNode {
    const id = `profile-${name}`;
    const error = fieldErrors[name];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label htmlFor={id} style={{ ...type.statLabel, color: colors.mutedInk }}>
          {labels[name]}
        </label>
        <input
          id={id}
          type={inputType}
          value={draft?.[name] ?? ''}
          readOnly={!isAdmin}
          aria-invalid={error !== undefined}
          onChange={(e) => {
            const value = e.target.value;
            setDraft((current) => (current ? { ...current, [name]: value } : current));
          }}
          style={input}
        />
        {error !== undefined ? (
          <div role="alert" style={{ ...type.caption, color: verdict.unknown.fg }}>
            {error}
          </div>
        ) : help ? (
          <div style={{ ...type.caption, color: colors.mutedInk }}>{help}</div>
        ) : null}
      </div>
    );
  }
}

const subtitle =
  'What a consumer sees when they scan, and what must be complete before you can issue codes';

function Checklist({ rows, onFix }: { rows: Check[]; onFix: (to: string) => void }) {
  const open = rows.filter((r) => !r.met).length;
  return (
    <section
      aria-labelledby="checklist-title"
      style={{ ...cardBox, padding: '20px 22px', display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <h2 id="checklist-title" style={{ ...type.panelTitle, color: colors.navy, margin: 0 }}>
          Before you can issue codes
        </h2>
        {open > 0 ? (
          <span style={{ ...chipStyle(verdict.suspect), alignSelf: 'center' }}>{open} to go</span>
        ) : null}
      </div>
      <ul aria-label="Checklist" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {/* The board's rows and rules are siblings in a 4px-gap column, so each rule carries 4px either side. */}
        {rows.map((row, i) => (
          <li key={row.key} data-met={row.met}>
            {i > 0 ? <div style={{ height: 1, background: colors.line, margin: '4px 0' }} /> : null}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 0' }}>
              {row.met ? <MetIcon /> : <OpenIcon />}
              <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ ...type.bodyStrong, color: row.met ? colors.mutedInk : colors.navy }}>
                  {row.title}
                </div>
                <div style={{ ...type.meta, color: colors.mutedInk, lineHeight: 1.5 }}>
                  {row.met ? row.value : row.detail}
                </div>
              </div>
              {!row.met && row.fix ? (
                <button
                  className="vp-link"
                  type="button"
                  onClick={() => onFix(row.fix!)}
                  aria-label={`Fix ${row.title.toLowerCase()}`}
                  style={fixButton}
                >
                  Fix
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      <p
        style={{
          borderTop: `1px solid ${colors.line}`,
          marginTop: 12,
          marginBottom: 0,
          paddingTop: 14,
          ...type.meta,
          color: colors.mutedInk,
          lineHeight: 1.55,
        }}
      >
        Printed codes cannot be recalled, so these are checked before a batch is minted, not after.
      </p>
    </section>
  );
}

function VerificationPanel(props: {
  organization: Organization;
  readiness: Readiness;
  isAdmin: boolean;
  busy: boolean;
  note: string;
  onNote: (note: string) => void;
  onAsk: () => void;
  refusal: string | null;
}) {
  const verification = verificationOf(props.organization, props.readiness);
  let body: ReactNode;
  switch (verification.state) {
    case 'verified':
      body = <span style={chipStyle(verdict.genuine)}>Verified manufacturer</span>;
      break;
    case 'requested':
      body = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={chipStyle({ bg: onNavy.awaiting, fg: verdict.suspect.fg })}>
            Awaiting review
          </span>
          <span style={{ ...type.caption, color: colors.mutedOnNavy }}>
            submitted {fullDate(new Date(verification.at))}
          </span>
        </div>
      );
      break;
    case 'blocked':
      body = (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={chipStyle(navyChip)}>Cannot request yet</span>
            {props.isAdmin ? (
              <span aria-disabled="true" style={{ ...type.metaBold, color: onNavy.dimmed }}>
                Request verification
              </span>
            ) : null}
          </div>
          {verification.gaps.map((gap) => (
            <div
              key={gap}
              style={{ ...type.caption, color: colors.mutedOnNavy, lineHeight: 1.5, marginTop: -4 }}
            >
              {gap}
            </div>
          ))}
        </>
      );
      break;
    case 'can_ask':
      body = (
        <>
          {/* Drawn stretched: an inline-flex in a column, as the board has it. */}
          <span style={{ ...chipStyle(navyChip), alignSelf: 'stretch' }}>Not yet verified</span>
          {props.isAdmin ? (
            <>
              <input
                aria-label="Licence or registry link"
                placeholder="Licence or registry link (optional)"
                value={props.note}
                maxLength={500}
                onChange={(e) => props.onNote(e.target.value)}
                style={noteInput}
              />
              <button
                className="vp-fill"
                type="button"
                disabled={props.busy}
                onClick={props.onAsk}
                style={askButton}
              >
                Request verification
              </button>
            </>
          ) : null}
        </>
      );
  }
  return (
    <section aria-labelledby="verification-title" style={navyPanel}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2 L20 5 V11 C20 16.5 16.5 20.5 12 22 C7.5 20.5 4 16.5 4 11 V5 Z"
            stroke={colors.mutedOnNavy}
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M8.5 12 L11 14.5 L15.5 9.5"
            stroke={colors.mutedOnNavy}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h2 id="verification-title" style={{ ...type.cardTitle, color: colors.white, margin: 0 }}>
          Verified manufacturer
        </h2>
      </div>
      <p style={{ ...type.meta, color: colors.mutedOnNavy, lineHeight: 1.55, margin: 0 }}>
        Veriplus staff check your legal entity against the public registry. Verified brands carry a
        badge on every scan. A counterfeiter cannot obtain one.
      </p>
      <div
        data-state={verification.state}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}
      >
        {body}
        {props.refusal !== null ? <Rejection>{props.refusal}</Rejection> : null}
      </div>
    </section>
  );
}

function MetIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Done"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="9" stroke={verdict.genuine.fg} strokeWidth="1.8" />
      <path
        d="M8.5 12.2 L11 14.7 L15.5 9.5"
        stroke={verdict.genuine.fg}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OpenIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-label="To do"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="9" stroke={verdict.suspect.fg} strokeWidth="1.8" />
      <path d="M12 7.5 V12.5" stroke={verdict.suspect.fg} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.2" r="1.2" fill={verdict.suspect.fg} />
    </svg>
  );
}

/** A save failure: `validate` keys details by `body.<field>`; the six drawn fields take theirs. */
function fromFields(error: unknown): {
  fields: Partial<Record<ProfileField, string>>;
  general?: string;
} {
  if (!(error instanceof ApiError) || error.details === undefined)
    return { fields: {}, general: failureText(error) };
  const fields: Partial<Record<ProfileField, string>> = {};
  const rest: string[] = [];
  for (const [path, message] of Object.entries(error.details)) {
    const name = path.replace(/^body\./, '');
    if ((profileFields as readonly string[]).includes(name)) fields[name as ProfileField] = message;
    else rest.push(message);
  }
  return rest.length > 0 ? { fields, general: rest.join(' ') } : { fields };
}

/** A refused request: the 409's per-field reasons when it names them, rule 7 applied. */
function askFailureText(error: unknown): string {
  if (error instanceof ApiError && error.details !== undefined) {
    const reasons = Object.values(error.details);
    if (reasons.length > 0) return reasons.map(plain).join(' ');
  }
  return failureText(error);
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return plain(error.message);
  return 'Reload the page to try again.';
}

const navyChip = { bg: onNavy.chip, fg: colors.mutedOnNavy };

const columns: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.15fr 1fr',
  gap: 16,
  alignItems: 'start',
};

const pair: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

const cardBox: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const input: CSSProperties = {
  height: 44,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

const navyPanel: CSSProperties = {
  borderRadius: radius.card,
  background: colors.navy,
  padding: '20px 22px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const noteInput: CSSProperties = {
  height: 40,
  boxSizing: 'border-box',
  borderRadius: 10,
  background: onNavy.field,
  border: `1px solid ${onNavy.fieldBorder}`,
  padding: '0 12px',
  ...type.meta,
  color: colors.mutedOnNavy,
};

const askButton: CSSProperties = {
  alignSelf: 'flex-start',
  padding: '10px 18px',
  borderRadius: 20,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  cursor: 'pointer',
  ...type.button,
  fontSize: '13px',
};

const chipStyle = (tone: { bg: string; fg: string }): CSSProperties => ({
  display: 'inline-flex',
  alignSelf: 'flex-start',
  padding: '4px 12px',
  borderRadius: radius.chip,
  background: tone.bg,
  color: tone.fg,
  ...type.captionBold,
});

const fixButton: CSSProperties = {
  ...type.metaBold,
  color: colors.brand,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  flexShrink: 0,
};

const actionButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};
